import { useEffect, useReducer, useRef, useState } from 'react';
import listingsReducer from '@Components/Listings/ListingsReducer';
import atoms from '@Components/Utilities/SandhillsAtoms';
import { dayNames, dayShortNames, gtmEventType, monthNames } from '@Components/Utilities/SandhillsConstants';
import { loadable } from '@Components/Utilities/SandhillsLoadable';
import { gtmPushEvent, sandhillsRequest, truncateString } from '@Components/Utilities/SandhillsUtility';
import translator from '@Components/Utilities/Translator';
import Backdrop from '@mui/material/Backdrop';
import CircularProgress from '@mui/material/CircularProgress';
import ready from 'document-ready';
import Countdown from 'react-countdown';
import { useRecoilValue, useRecoilState } from 'recoil';

const AlertPreferenceModal = loadable(() => import('@Components/Shared/Modals/AlertPreferenceModal'), true);
const BiddingAgreementModal = loadable(() => import('@Components/Shared/Modals/BiddingAgreementModal'), true);

const EventSection = (props) => {
    const EVENTS_UPDATE_INTERVAL = 60000;
    const MAX_DESCRIPTION_LENGTH = 225;

    const eventsTimer = useRef(null);
    const routes = useRecoilValue(atoms.routes);
    
    const [eventIDForAlertPreferenceModal, setEventIDForAlertPreferenceModal] = useState(0);
    const [showAlertPreferenceModal, setShowAlertPreferenceModal] = useState(false);
    const [notificationSettings, setNotificationSettings] = useState(null);
    
    const [biddingAgreementData, setBiddingAgreementData] = useState({});
    const [biddingAgreementProps, setBiddingAgreementProps] = useState(null);
    const [isLoading, setIsLoading] = useState(false);
    const [showBiddingAgreementModal, setShowBiddingAgreementModal] = useState(false);

    const [listEvents, dispatchListEvents] = useReducer(listingsReducer, props.Events);
    const [currentUser, setCurrentUser] = useRecoilState(atoms.currentUser);

    /* ALERT PREFERENCES */
    const openAlertPreferenceModal = (eventID) => {
        if (!currentUser.IsAuthenticated) {
            window.location.href = routes.SSO;
        }

        if (eventID && eventID > 0) {
            setEventIDForAlertPreferenceModal(eventID);
        }

        if (!isLoading) {
            setIsLoading(true);
            sandhillsRequest({
                url: routes.GetUserAuctionAlertPreferences,
                method: 'post',
                errorHandling: {
                    useConsole: false,
                    useAlert: false
                }
            }).then((data) => {
                setIsLoading(false);
                if (data) {
                    setNotificationSettings(data);
                    setShowAlertPreferenceModal(!showAlertPreferenceModal);
                }
            }).catch(() => {
                setIsLoading(false);
            });
        }
    };

    /* T&C */
    const toggleBiddingAgreementModal = (data) => {
        !showBiddingAgreementModal ? setBiddingAgreementProps(data) : setBiddingAgreementProps(null);
        setShowBiddingAgreementModal(!showBiddingAgreementModal);
    };

    const onBiddingAgreementDataUpdated = (biddingAgreementDataUpdated) => {
        setBiddingAgreementData(biddingAgreementDataUpdated);
        toggleBiddingAgreementModal?.({
            biddingAgreement: biddingAgreementDataUpdated,
            onBiddingAgreementDataUpdated: onBiddingAgreementDataUpdated
        });
    };

    const loadEventBiddingAgreement = (eventID) => {
        if (!isLoading) {
            if (biddingAgreementData.hasOwnProperty(eventID)) {
                toggleBiddingAgreementModal?.({
                    biddingAgreement: biddingAgreementData[eventID],
                    onBiddingAgreementDataUpdated: onBiddingAgreementDataUpdated
                });
            } else {
                if (typeof window !== 'undefined') {
                    setIsLoading(true);
                    sandhillsRequest({
                        url: routes.GetBiddingAgreement + '/' + eventID
                    }).then((data) => {
                        if (data?.BidAgreement) {
                            setBiddingAgreementData(previousState => ({
                                ...previousState,
                                [eventID]: data
                            }));
                            setIsLoading(false);
                            toggleBiddingAgreementModal?.({
                                biddingAgreement: data,
                                onBiddingAgreementDataUpdated: onBiddingAgreementDataUpdated
                            });
                        }
                    }).catch((error) => {
                        console.error('An error occured while retrieving terms and conditions...', error);
                        setIsLoading(false);
                    });
                }

            }
        }
    };

    /* MISC. */
    const isEventEnded = (eventProgress) => props.IsLiveEventSection && eventProgress >= 100;

    const isSaleCatalogEmpty = (totalItems) => totalItems == 0;

    // Rare case, but if a live event doesn't have any listings, then it should redirect to that sale's catalog page
    const isLiveSaleCatalogSet = (event) => props.IsLiveEventSection && !isEventEnded(event.Progress) && !isSaleCatalogEmpty(event.TotalItems);

    // "Ended" events' sale catalogs should direct to the "auction-results" URL, otherwise, always direct to "upcoming-auctions" URL
    const getSaleCatalogUrl = (event) => isEventEnded(event.Progress) ? event.SaleCatalogUrl.replace("upcoming-auctions", "auction-results") : event.SaleCatalogUrl;

    const getEventDescription = (event) => {
        if (event.Description.length > MAX_DESCRIPTION_LENGTH) {
            return (
                <>
                    {truncateString(event.Description, MAX_DESCRIPTION_LENGTH)}{'\u00A0'}
                    <a target='_blank' href={isLiveSaleCatalogSet(event) ? event.AuctionBoardUrl : getSaleCatalogUrl(event)}>
                        {translator.translate('sandhills.viewmore')}
                        <span className='visually-hidden'>({translator.translate('sandhills.opensinanewtab')})</span>
                    </a>
                </>
            );
        }

        return event.Description;
    };

    const countDownRenderer = ({ days, hours, minutes, seconds, completed }) => {
        if (completed) {
            return translator.translate('sandhills.tradepubwebsite.mvc.auctionislive');
        } else {
            let countDown = '';

            if (days > 0) {
                countDown += days + ' ' + (days > 1 ? translator.translate('sandhills.days') : translator.translate('sandhills.day')) + ' ';
            }
            if (hours > 0) {
                countDown += hours + ' ' + (hours > 1 ? translator.translate('sandhills.hours') : translator.translate('sandhills.hour')) + ' ';
            }
            if (minutes > 0) {
                var minString = translator.translate('sandhills.min').toString();
                countDown += minutes + ' ' + minString.charAt(0).toUpperCase() + minString.slice(1);
            }
            if (days == 0 && hours == 0) {
                if (seconds >= 0) {
                    countDown += ' ' + seconds + ' ' + translator.translate('sandhills.seconds');
                }
            }

            countDown = countDown.trim();

            return (
                <span>{countDown}</span>
            );
        }
    };

    const formatDate = (dateString) => {
        if (dateString) {
            let date = new Date(dateString);

            // Month
            let monthIndex = date.getUTCMonth();
            let monthName = translator.translate('sandhills.crm.dscrm.' + monthNames[monthIndex]);

            // Day of the week
            let dayIndex = date.getUTCDay();
            let dayName = translator.translate('sandhills.crm.dscrm.' + dayNames[dayIndex]);

            return dayName + ', ' + monthName + ' ' + date.getUTCDate() + ', ' + date.getUTCFullYear();
        }

        return '';
    };

    const removeFromWatchlist = (Id) => {
        sandhillsRequest({
            url: routes.EventWatchlistRemove,
            method: 'post',
            data: {
                eventID: Id
            },
            errorHandling: {
                showDetails: false,
                useConsole: false
            }
        }).then((data) => {
            if (data) {
                var eventsUpdate = listEvents.map(e => { if (e.Id === Id) { e.IsSavedToWatchList = false; return e } else { return e; } });
                dispatchListEvents({
                    type: 'update-listings',
                    payload: [...eventsUpdate]
                });
            }
        });
    }

    const addToWatchlist = (Id) => {
        sandhillsRequest({
            url: routes.EventWatchlistAdd,
            method: 'post',
            data: {
                eventID: Id
            },
            errorHandling: {
                showDetails: false,
                useConsole: false
            }
        }).then((data) => {
            if (data) {
                var eventsUpdate = listEvents.map(e => { if (e.Id === Id) { e.IsSavedToWatchList = true; return e } else { return e; } });
                dispatchListEvents({
                    type: 'update-listings',
                    payload: [...eventsUpdate]
                });
            }
        });
    }
    const toggleWatchlist = (event) => {
        if (!currentUser.IsAuthenticated) {
            window.location.href = routes.SSO + '?to=' + encodeURIComponent('https://' + window.location.hostname + routes.EventWatchlistAdd + '?EventID=' + event.Id + '&to=' + encodeURIComponent(window.location.href));
        }

        if (event.IsSavedToWatchList) {
            removeFromWatchlist(event.Id);
        } else {
            addToWatchlist(event.Id);
        }
    }

    const renderEvent = (event) => {
        return (
            <div className={'live-event' + (event.IsAuctionTime ? ' at-event' : '')} key={event.Id}>
                <div className='live-date'>
                    {props.IsLiveEventSection ?
                        <div className='live'>
                            {event.IsAuctionTime ?
                                <>
                                    <img src={props.AuctionTimeStatusLogoUrl} alt="" />
                                    {translator.translate('sandhills.online').toString().toUpperCase()}
                                </> : isEventEnded(event.Progress) ?
                                    <>
                                        <i className='fas fa-gavel' />
                                        {translator.translate('sandhills.ended').toString().toUpperCase()}
                                    </> :
                                    <>
                                        <i className='fas fa-rss' />
                                        {translator.translate('sandhills.tradepubwebsite.mvc.live').toString().toUpperCase()}
                                    </>
                            }
                        </div>
                        :
                        <div className='calendar'>
                            {dayShortNames[new Date(event.DisplayAuctionDateOnly)?.getUTCDay()]}
                            <strong>{new Date(event.DisplayAuctionDateOnly)?.getUTCDate()}</strong>
                        </div>
                    }
                </div>

                <div className='live-image'>
                    {event.IsAuctionTime ?
                        <a target='_blank' href={event.AuctionTimeUrl}>
                            <img src={event.LogoUrl} alt='AuctionTime Online Auction' />
                            <span className='visually-hidden'>({translator.translate('sandhills.opensinanewtab')})</span>
                        </a>
                        :
                        <a target='_blank' href={isLiveSaleCatalogSet(event) ? event.AuctionBoardUrl : getSaleCatalogUrl(event)}>
                            <img src={event.LogoUrl} alt={event.AuctioneerName} />
                            <span className='visually-hidden'>({translator.translate('sandhills.opensinanewtab')})</span>
                        </a>
                    }
                </div>

                <div className='live-details'>
                    <h2>
                        {event.IsAuctionTime ?
                            <>
                                <a target='_blank' href={event.AuctionTimeUrl}>
                                    {translator.translate('sandhills.auctiontimeonlineauctionstitle')}
                                    <span className='visually-hidden'>({translator.translate('sandhills.opensinanewtab')})</span>
                                </a>
                            </> :
                            <>
                                <a target='_blank' href={isLiveSaleCatalogSet(event) ? event.AuctionBoardUrl : getSaleCatalogUrl(event)}>
                                    {event.EventTitle}
                                    <span className='visually-hidden'>({translator.translate('sandhills.opensinanewtab')})</span>
                                </a>
                                <div className='watchlist'>
                                    <div className='watchlist-toggle' role='button' tabIndex='0' onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); toggleWatchlist(event); }}} aria-label={event.IsSavedToWatchList ? translator.translate('sandhills.removefromwatchlistwithspace') : translator.translate('sandhills.addtowatchlistwithspace') } onClick={ (e) => toggleWatchlist(event) }>
                                        <i className={event.IsSavedToWatchList ? 'fas fa-heart' : 'far fa-heart'} />
                                    </div>
                                </div>
                            </>
                        }
                    </h2>

                    {props.IsLiveEventSection ?
                        <>
                            {event.IsAuctionTime ?
                                <>
                                    <strong>{translator.translate('sandhills.phone')}:{'\u00A0'}</strong>
                                    {props.AuctionTimePhone}
                                </> :
                                <>
                                    <strong className='live-auctioneer-name'>{event.AuctioneerName}</strong>
                                    {new Date(event.StartDateTime)?.getTime() <= new Date() ?
                                        <>
                                            {!isEventEnded(event.Progress) &&
                                                <>
                                                    <strong> - {event.StartTime}</strong>
                                                    <div className='meter pbred'>
                                                        <span style={{ width: `${event.Progress ?? 0}%` }}>
                                                            <strong style={{ display: 'block' }}>{event.Progress ?? 0}%</strong>
                                                        </span>
                                                    </div>
                                                    <div>
                                                        <p>{translator.translate('sandhills.description')}: {getEventDescription(event)}</p>
                                                    </div>
                                                </>
                                            }
                                        </>
                                        :
                                        <>
                                            <div>
                                                {event.StartTime} - {translator.translate('sandhills.tradepubwebsite.mvc.auctionbeginsin')}{'\u00A0'}
                                                <span className='red'>
                                                    <Countdown date={new Date(event.StartDateTime)?.getTime()} renderer={countDownRenderer} />
                                                </span>
                                            </div>
                                        </>
                                    }
                                </>
                            }
                        </> :
                        <>
                            {event.IsAuctionTime ?
                                <>
                                    {formatDate(event.DisplayAuctionDateOnly)}
                                </> :
                                <>
                                    <strong className='live-auctioneer-name'>{event.AuctioneerName}</strong>
                                    <div>
                                        {event.StartTime} - {translator.translate('sandhills.tradepubwebsite.mvc.auctionbeginsin')}{'\u00A0'}
                                        <span className='red'>
                                            <Countdown date={new Date(event.StartDateTime)?.getTime()} renderer={countDownRenderer} />
                                        </span>
                                    </div>
                                </>
                            }
                        </>
                    }
                </div>

                <div className='live-btns'>
                    {props.IsLiveEventSection ?
                        <>
                            {isEventEnded(event.Progress) && event.IsEquipmentFacts ?
                                <>
                                    <a className='ts-button red-btn' target='_blank' href={getSaleCatalogUrl(event)}>{translator.translate('sandhills.viewresults')} <span className='visually-hidden'>({translator.translate('sandhills.opensinanewtab')})</span></a>
                                </> :
                                <>
                                    {event.IsAuctionTime ?
                                        <>
                                            <a className='ts-button red-btn' target='_blank' href={event.AuctionTimeUrl}>
                                                {translator.translate('sandhills.viewdetails')}
                                                <span className='visually-hidden'>({translator.translate('sandhills.opensinanewtab')})</span>
                                            </a>
                                        </> :
                                        <>
                                            <div className='live-address'>
                                                {event.IsEquipmentFactsVirtualEvent ? (
                                                    <strong>{event.FormattedLocation.toString().toUpperCase()}</strong>
                                                ) : (
                                                    <strong>{event.City}, {event.State}</strong>
                                                )}
                                                <br />
                                                <strong>{translator.translate('sandhills.phone')}:{'\u00A0'}</strong>
                                                <a className='phone-number' href={'tel:' + (event.FormattedPhoneNumber)} onClick={() => gtmPushEvent(gtmEventType.call)}>
                                                    {event.FormattedPhoneNumber}
                                                </a>
                                            </div>
                                            <a className='ts-button red-btn' target='_blank' href={isLiveSaleCatalogSet(event) ? event.AuctionBoardUrl : getSaleCatalogUrl(event)}>{translator.translate('sandhills.enterauction')} <span className='visually-hidden'>({translator.translate('sandhills.opensinanewtab')})</span></a>
                                            <br />
                                            <a className='ts-button sales-btn' target='_blank' href={getSaleCatalogUrl(event)}>
                                                {translator.translate('sandhills.auctioncatalog')}
                                                <span className='visually-hidden'>({translator.translate('sandhills.opensinanewtab')})</span>
                                            </a>
                                            <a role='button' tabIndex='0' onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); loadEventBiddingAgreement(event.Id); }}} aria-label={translator.translate('sandhills.tradepubwebsite.mvc.viewbiddingagreement')} className='ts-button terms-btn' onClick={() => loadEventBiddingAgreement(event.Id)}>
                                                <i className='fas fa-file-shield' />
                                            </a>
                                        </>
                                    }
                                </>
                            }
                        </> :
                        <>
                            {event.IsAuctionTime ?
                                <>
                                    <a className='ts-button red-btn' target='_blank' href={event.AuctionTimeUrl}>
                                        {translator.translate('sandhills.viewdetails')}
                                        <span className='visually-hidden'>({translator.translate('sandhills.opensinanewtab')})</span>
                                    </a>
                                </> :
                                <>
                                    {isSaleCatalogEmpty(event.TotalItems) ?
                                        <>
                                            <a role='button' tabIndex='0' onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); openAlertPreferenceModal(event.Id) }}} className='ts-button no-catalog-btn' onClick={() => openAlertPreferenceModal(event.Id)}>
                                                {translator.translate('sandhills.catalogcomingsoon')}!
                                                <br />
                                                <span style={{ textDecoration: 'underline' }}>{translator.translate('sandhills.getnotified')}</span>
                                            </a>
                                        </> :
                                        <>
                                            <a className='ts-button sales-btn' target='_blank' href={getSaleCatalogUrl(event)}>
                                                {translator.translate('sandhills.startbidding')}
                                                <span className='visually-hidden'>({translator.translate('sandhills.opensinanewtab')})</span>
                                            </a>
                                            <a role='button' tabIndex='0' onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); loadEventBiddingAgreement(event.Id); }}} aria-label={translator.translate('sandhills.tradepubwebsite.mvc.viewbiddingagreement')} className='ts-button terms-btn' onClick={() => loadEventBiddingAgreement(event.Id)}>
                                                <i className='fas fa-file-shield' />
                                            </a>
                                        </>
                                    }
                                </>
                            }
                        </>
                    }
                </div>
            </div>
        );
    }

    return (
        <div className='events-section'>
            {props.IsLiveEventSection ?
                <div className='section-title'>
                    <h1 className='section-title-text'>
                        {props.SectionTitle}
                    </h1>
                </div>
                :
                <div className='section-title'>
                    {props.NoLiveEvents ?
                        <h1 className='section-title-text'>
                            {props.SectionTitle}
                        </h1>
                        :
                        <h2 className='section-title-text'>
                            {props.SectionTitle}
                        </h2>
                    }
                </div>
            }
            <div className={'live-event-list' + (props.IsLiveEventSection ? ' live-active' : '')}>
                {listEvents && listEvents.length > 0 &&
                    listEvents.map((event) => renderEvent(event))}
            </div>

            {!props.IsLiveEventSection &&
                <div className='upcoming-pagination'>
                    <span dangerouslySetInnerHTML={{ __html: props.UpcomingPaginationTitle }} />
                    <br />
                    <a className='ts-button view-all-btn' href={props.UpcomingEventsUrl}>
                        {translator.translate('sandhills.tradepubwebsite.mvc.viewall')}
                    </a>
                </div>
            }

            {(showAlertPreferenceModal && eventIDForAlertPreferenceModal) &&
                <AlertPreferenceModal
                    {...notificationSettings}
                    onClose={(e) => { setShowAlertPreferenceModal(false); }}
                    EventID={eventIDForAlertPreferenceModal}
                />}

            {(showBiddingAgreementModal && biddingAgreementProps) &&
                <BiddingAgreementModal
                    {...biddingAgreementProps}
                    onClose={toggleBiddingAgreementModal}
                    CAPrivacyPolicyNotice={props.CAPrivacyPolicyNotice}
                />}

            <Backdrop
                open={isLoading}
                sx={{ zIndex: (theme) => theme.zIndex.drawer + 1 }}>
                <CircularProgress />
            </Backdrop>
        </div>
    );
}

export default EventSection;